import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  startWith,
  map,
  Subscription,
} from 'rxjs';
import { CompanySetupService } from '../company-setup.service';
import { CompanySetupAddressComponent } from './company-setup-address/company-setup-address.component';
import { CompanySetupInfoComponent } from './company-setup-info/company-setup-info.component';
import { CompanySetupSocialMediaComponent } from './company-setup-social-media/company-setup-social-media.component';

@Component({
  selector: 'app-company-setup-signin',
  templateUrl: './company-setup-signin.component.html',
  styleUrls: ['./company-setup-signin.component.scss'],
})
export class CompanySetupSigninComponent implements OnInit, OnDestroy {
  // Inicializado no ngOnInit a partir do service para sobreviver à re-criação do componente
  currentStep: BehaviorSubject<number> = new BehaviorSubject(0);
  currentStep$: Observable<number> = this.currentStep.asObservable();

  private stepSub: Subscription;

  steps: any[] = [
    {
      component: CompanySetupInfoComponent,
      title: 'Dados da empresa',
      description: 'Informe os dados pertinentes a sua empresa',
    },
    {
      component: CompanySetupAddressComponent,
      title: 'Endereço',
      description: 'Informe o endereço da empresa',
    },
    {
      component: CompanySetupSocialMediaComponent,
      title: 'Redes Sociais',
      description: 'Informe as redes sociais da sua empresa',
    },
  ];

  isValid$: Observable<boolean> = combineLatest([
    this.companySetupService.infoForm.statusChanges.pipe(startWith('INVALID')),
    this.companySetupService.addressForm.statusChanges.pipe(
      startWith('INVALID')
    ),
    this.companySetupService.socialMediaForm.statusChanges.pipe(
      startWith('INVALID')
    ),
    this.currentStep$,
  ]).pipe(
    map(
      ([infoFormValid, addressFormValid, socialMediaFormValid, step]) =>
        (infoFormValid === 'VALID' && step === 0) ||
        (addressFormValid === 'VALID' && step === 1) ||
        (socialMediaFormValid === 'VALID' && step === 2)
    )
  );

  constructor(private companySetupService: CompanySetupService) { }

  ngOnInit(): void {
    // Restaura o sub-passo salvo no service (evita reset para CNPJ ao voltar da tela de plano)
    this.currentStep.next(this.companySetupService.signinStep);

    // Persiste o sub-passo a cada mudança para sobreviver à destruição do componente
    this.stepSub = this.currentStep$.subscribe(step => {
      this.companySetupService.signinStep = step;
    });
  }

  ngOnDestroy(): void {
    this.stepSub?.unsubscribe();
  }

  nextStep() {
    const forms = [
      this.companySetupService.infoForm,
      this.companySetupService.addressForm,
      this.companySetupService.socialMediaForm,
    ];
    const currentForm = forms[this.currentStep.value];

    // Marca todos os campos como tocados para exibir os warnings de validação
    if (currentForm?.invalid) {
      currentForm.markAllAsTouched();
      return;
    }

    if (this.currentStep.value !== 2) {
      this.currentStep.next(this.currentStep.value + 1);
      return;
    }

    // Último sub-passo: se a empresa já foi criada anteriormente (usuário voltou
    // da tela de plano para editar), apenas avança sem chamar createCompany() de novo.
    if (this.companySetupService.companyId) {
      this.companySetupService.currentComponentIndex.next(1);
      return;
    }

    this.companySetupService.createCompany().subscribe({
      next: (company: any) => {
        this.companySetupService.currentComponentIndex.next(1);
        this.companySetupService.companyId = company.id;
        this.companySetupService.contextId = company.contextId;
        this.companySetupService.companyType = company.type.sigla;
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  prevStep() {
    this.currentStep.next(this.currentStep.value - 1);
  }
}
